import React from 'react'
import { Radio, Checkbox } from 'antd'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import scrollIntoView from 'dom-scroll-into-view'
import { findDOMNode } from 'react-dom'

import MenuItem from './MenuItem'

import _map from 'lodash/map'
import get from 'lodash/get'
import difference from 'lodash/difference'

const RadioGroup = Radio.Group
const CheckboxGroup = Checkbox.Group

class AntDropdownContent extends React.PureComponent {
  menuRef = React.createRef()
  selectedMenuItemRef = React.createRef()
  perPaginationStepEntries = 100
  paginationDistance = 200

  state = {
    showEntries: this.perPaginationStepEntries,
  }

  haveSegmentsDifference = (oldSegments, newSegments) => {
    const oldSegmentOptions = get(oldSegments[0], 'options')
    const newSegmentOptions = get(newSegments[0], 'options')

    if (oldSegmentOptions.length !== newSegmentOptions.length) { return true }

    return difference(_map(oldSegmentOptions, 'id'), _map(oldSegmentOptions, 'id')).length > 0
  }

  componentDidUpdate (prevProps) {
    const currentProps = this.props

    if (this.menuRef.current && this.selectedMenuItemRef.current) {
      // TODO: find solution to get dom-scroll-into-view working without the actual node but with a ref
      scrollIntoView(
        findDOMNode(this.selectedMenuItemRef.current), // eslint-disable-line react/no-find-dom-node
        findDOMNode(this.menuRef.current), // eslint-disable-line react/no-find-dom-node
        {
          onlyScrollIfNeeded: true,
        },
      )
    }

    if (
      this.props.pagination &&
      this.haveSegmentsDifference(prevProps.filteredSegments, currentProps.filteredSegments)
    ) {
      this.setState({ showEntries: this.perPaginationStepEntries })
    }
  }

  renderRadioGroup = (segment, key) => {
    const {
      handleFilterChange,
    } = this.props

    return (
      <li
        className={classNames(
          'ant-dropdown-menu-item-group',
          'ant-dropdown-menu-item-group--no-margin',
          {
            'ant-dropdown-menu-item-group--no-title': !segment.title,
          },
        )}
        key={key}
      >
        <div className="ant-dropdown-menu-item-group-title">
          {segment.title}
        </div>

        <ul className="ant-dropdown-menu-item-group-list">
          <li className="ant-dropdown-menu-item">
            <RadioGroup
              options={segment.options.map((option) => {
                return {
                  label: option.text,
                  value: option.id,
                }
              })}
              value={segment.currentValue}
              onChange={(event) => { handleFilterChange(event.target.value, segment, false) }}
            />
          </li>
        </ul>
      </li>
    )
  }

  renderCheckboxGroup = (segment, key) => {
    const {
      handleFilterChange,
    } = this.props

    return (
      <li
        className={classNames(
          'ant-dropdown-menu-item-group',
          'ant-dropdown-menu-item-group--no-margin',
          {
            'ant-dropdown-menu-item-group--no-title': !segment.title,
          },
        )}
        key={key}
      >
        <div className="ant-dropdown-menu-item-group-title">
          {segment.title}
        </div>

        <ul className="ant-dropdown-menu-item-group-list">
          <li className="ant-dropdown-menu-item">
            <CheckboxGroup
              options={segment.options.map((option) => {
                return {
                  label: option.text,
                  value: option.id,
                }
              })}
              value={segment.currentValue}
              onChange={(checkedValues) => {
                handleFilterChange(checkedValues, segment, false)
              }}
            />
          </li>
        </ul>
      </li>
    )
  }

  renderMenuSegment = (segment, segmentindex) => {
    const key = `segment-${segmentindex}`

    if (segment.type === 'radiobuttons') { return this.renderRadioGroup(segment, key) }
    if (segment.type === 'checkboxes') { return this.renderCheckboxGroup(segment, key) }

    const segmentOptions = this.props.pagination ? segment.options.slice(0, this.state.showEntries) : segment.options

    return segmentOptions.map((menuItem, index) => {
      if (menuItem.children) {
        return this.renderMenuItemGroup(menuItem, index, segment)
      }

      return this.renderMenuItem(menuItem, index, segment)
    })
  }

  renderMenuItem = (menuItem, index, segment) => {
    const {
      userSelectionActive,
      selectedOption,
      handleLinkClick,
      handleFilterChange,
      setSelectedIndex,
      handleItemMouseDown,
      handleItemMouseUp,
      Link,
      urlTransformer,
    } = this.props

    if (!menuItem.text) { return }

    let selected

    if (userSelectionActive && selectedOption) {
      selected = selectedOption.segmentName === segment.name && selectedOption.optionId === menuItem.id
    } else {
      selected = segment.currentValue === menuItem.id
    }

    return (
      <MenuItem
        key={index}
        menuItem={menuItem}
        segment={segment}
        selected={selected}
        handleLinkClick={handleLinkClick}
        handleFilterChange={handleFilterChange}
        handleMouseDown={handleItemMouseDown}
        handleMouseUp={handleItemMouseUp}
        setSelectedIndex={setSelectedIndex}
        selectedMenuItemRef={this.selectedMenuItemRef}
        Link={Link}
        urlTransformer={urlTransformer}
      />
    )
  }

  renderMenuItemGroup = (menuItemGroup, index, segment) => {
    return (
      <li
        className={classNames(
          'ant-dropdown-menu-item-group',
          {
            'ant-dropdown-menu-item-group--no-title': !menuItemGroup.text,
          },
        )}
        key={`itemgroup-${index}`}
      >
        <div className="ant-dropdown-menu-item-group-title">
          {menuItemGroup.text}
        </div>

        <ul className="ant-dropdown-menu-item-group-list">
          {menuItemGroup.children.map((menuItem, index) => this.renderMenuItem(menuItem, index, segment))}
        </ul>
      </li>
    )
  }

  renderEmptyMenuItem = () => {
    const {
      noEntriesText,
    } = this.props

    return (
      <li className="ant-dropdown-menu-item-group" key="empty">
        <div className="ant-dropdown-menu-item-group-title ant-dropdown-menu-item-group-title--no-padding-bottom">
          {noEntriesText}
        </div>
      </li>
    )
  }

  onScroll = (event) => {
    if (!this.props.pagination) { return }

    const menuContent = event.currentTarget
    const scrollBottom = menuContent.offsetHeight + menuContent.scrollTop

    if (menuContent.scrollHeight - scrollBottom < this.paginationDistance) {
      this.setState({ showEntries: this.state.showEntries + this.perPaginationStepEntries })
    }
  }

  render () {
    const {
      filteredSegments,
      hasNoEntries,
    } = this.props

    const renderSegments = []

    if (hasNoEntries) {
      renderSegments.push(this.renderEmptyMenuItem())
    } else {
      filteredSegments.filter((segment) => !segment.hidden).forEach((segment, index) => {
        renderSegments.push(this.renderMenuSegment(segment, index))
      })
    }

    return (
      <ul className="ant-dropdown-menu" ref={this.menuRef} onScroll={this.onScroll}>
        {renderSegments}
      </ul>
    )
  }
}

AntDropdownContent.propTypes = {
  filteredSegments: PropTypes.array.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  handleLinkClick: PropTypes.func.isRequired,
  handleItemMouseDown: PropTypes.func,
  handleItemMouseUp: PropTypes.func,
  selectedOption: PropTypes.object,
  userSelectionActive: PropTypes.bool.isRequired,
  setSelectedIndex: PropTypes.func.isRequired,
  hasNoEntries: PropTypes.bool,
  noEntriesText: PropTypes.string,
  pagination: PropTypes.bool, // NOTE: for now this only supports 'flat' entries (with just one menusegmentgroup) and no radio/checkbox groups
  Link: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  urlTransformer: PropTypes.func,
}

export default AntDropdownContent
